import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useFarmData } from '../context/FarmDataContext';
import { useFarmSelection } from '../context/FarmSelectionContext';
import { emphasize, styled, useTheme } from '@mui/material/styles';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Chip from '@mui/material/Chip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MapIcon from '@mui/icons-material/Map';
import useMediaQuery from '@mui/material/useMediaQuery';
import Logo from '../assets/headerlogo.png';
import IconButton from '@mui/material/IconButton';

const StyledBreadcrumb = styled(Chip)(({ theme }) => ({
    backgroundColor: emphasize('#181F2A', 0.06),
    height: 32,
    color: '#AAB4C8',
    fontWeight: 400,
    fontSize: 14,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    px: 1.5,
    '&:hover, &:focus': {
        backgroundColor: emphasize('#181F2A', 0.12),
    },
    '&:active': {
        boxShadow: theme.shadows[1],
        backgroundColor: emphasize('#181F2A', 0.06),
    },
}));

const MapBreadcrumb = styled(Chip)(({ theme }) => ({
  backgroundColor: emphasize('#181F2A', 0.06),
  color: '#AAB4C8',
  height: 32,
  fontWeight: 400,
  fontSize: 14,
  borderRadius: 4,
  px: 1.5,
  ml: 0.5,
  '& .MuiSvgIcon-root': {
    marginRight: theme.spacing(0.5),
    fontSize: 18,
  },
  boxShadow: 'none',
  border: '1px solid #283144',
}));

const CustomDateInput = React.forwardRef(({ value, onClick, placeholder, height = 32 }, ref) => (
  <TextField
    value={value}
    onClick={onClick}
    inputRef={ref}
    placeholder={placeholder}
    size="small"
    sx={{
      width: 150,
      bgcolor: '#222C2F',
      borderRadius: 1,
      input: {
        color: '#AAB4C8',
        fontWeight: 400,
        fontSize: 14,
        height: height,
        padding: '4px 8px',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
      },
      '& fieldset': { border: 'none' },
      mr: 0.5,
      height: height,
      p: 0,
      boxShadow: 'none !important',
      minHeight: height,
      maxHeight: height,
    }}
    InputProps={{
      endAdornment: (
        <InputAdornment position="end">
          <CalendarTodayIcon sx={{ color: '#AAB4C8', fontSize: 16 }} />
        </InputAdornment>
      ),
      readOnly: true,
    }}
  />
));

// Searchable Menu Component
const SearchableMenu = ({ anchorEl, open, onClose, searchValue, onSearchChange, searchPlaceholder, children, maxHeight = 400 }) => {
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          maxHeight: maxHeight,
          bgcolor: '#222C2F',
          border: '1px solid #283144',
          borderRadius: 1,
          mt: 0.5,
          '& .MuiMenuItem-root': {
            color: '#AAB4C8',
            fontSize: 14,
            py: 1,
            px: 2,
            '&:hover': {
              bgcolor: '#181F2A',
            },
            '&:focus': {
              bgcolor: '#181F2A',
            },
          },
        }
      }}
    >
      <Box sx={{ p: 1, borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
        <TextField
          size="small"
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-root': {
              color: '#AAB4C8',
              bgcolor: '#181F2A',
              borderRadius: 1,
              '& fieldset': {
                borderColor: '#283144',
              },
              '&:hover fieldset': {
                borderColor: '#AAB4C8',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#F9AA33',
              },
            },
            '& .MuiInputBase-input': {
              fontSize: 14,
              py: 1,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#AAB4C8', fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box sx={{ maxHeight: maxHeight - 80, overflow: 'auto' }}>
        {children}
      </Box>
    </Menu>
  );
};

const FarmsNav = ({ farm_id, idToken, setStartDate, setEndDate }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { farmData } = useFarmData();
    const { farmsList, currentFarm, setCurrentFarm } = useFarmSelection();
    const isMobile = useMediaQuery('(max-width:600px)');

    // Check if we're on crops or traceability pages to hide crop selector
    const isCropsPage = location.pathname.includes('/crops');
    const isTraceabilityPage = location.pathname.includes('/traceability');
    const hideCropSelector = isCropsPage || isTraceabilityPage;

    // Use currentFarm from context, or find by farm_id from farmsList
    const selectedFarm = currentFarm || farmsList.find(f => f.id === farm_id && !f.deleted) || {};
    const sections = (selectedFarm.sections || selectedFarm.zones || []).filter(s => !s.deleted);
    const section_id = location.state?.section_id;
    const selectedSection = sections.find(s => s.id === section_id) || {};
    
    // Get plots based on selection: if no section selected, show all plots from all zones
    const plots = section_id 
        ? (selectedSection.plots || []).filter(p => !p.deleted)
        : sections.flatMap(section => (section.plots || []).filter(p => !p.deleted));
    
    const plot_id = location.state?.plot_id;
    const selectedPlot = plots.find(p => p.id === plot_id) || {};
    const crop_id = location.state?.crop_id;
    const crops = farmData?.crops?.filter(crop => crop.plot_id === selectedPlot?.id && !crop.deleted) || [];

    // Calculate last harvest date for a crop
    const getLastHarvestDate = (crop) => {
        if (!farmData?.harvests) return null;
        const cropHarvests = farmData.harvests.filter(h => h.crop_id === crop.id);
        if (cropHarvests.length === 0) return null;
        
        const lastHarvest = cropHarvests.reduce((latest, h) => {
            const date = new Date(h.date);
            return date > latest ? date : latest;
        }, new Date(0));
        
        return lastHarvest;
    };

    // Get crop date range (transplant to last harvest)
    const getCropDateRange = (crop) => {
        if (!crop.transplant_date) return null;
        
        const transplantDate = new Date(crop.transplant_date);
        const lastHarvestDate = getLastHarvestDate(crop);
        
        if (!lastHarvestDate) return null;
        
        return {
            start: transplantDate,
            end: lastHarvestDate
        };
    };

    // Menu state
    const [anchorEl, setAnchorEl] = useState({});
    const handleMenuOpen = (key, event) => {
        setAnchorEl({ ...anchorEl, [key]: event.currentTarget });
        clearSearch(key);
    };
    const handleMenuClose = (key) => {
        setAnchorEl({ ...anchorEl, [key]: null });
        clearSearch(key);
    };

    // Search state
    const [searchValues, setSearchValues] = useState({
        farm: '',
        section: '',
        plot: '',
        crop: ''
    });

    const handleSearchChange = (key, value) => {
        setSearchValues(prev => ({ ...prev, [key]: value }));
    };

    const clearSearch = (key) => {
        setSearchValues(prev => ({ ...prev, [key]: '' }));
    };

    // Filter functions
    const getFilteredFarms = () => {
        const farms = farmsList.filter(f => !f.deleted).sort((a, b) => {
            const aName = a.name || a.farm_name || `Farm ${a.id}`;
            const bName = b.name || b.farm_name || `Farm ${b.id}`;
            return aName.localeCompare(bName);
        });
        if (!searchValues.farm) return farms;
        return farms.filter(farm => {
            const farmName = (farm.name || farm.farm_name || `Farm ${farm.id}`).toLowerCase();
            return farmName.includes(searchValues.farm.toLowerCase());
        });
    };

    const getFilteredSections = () => {
        const sectionsList = sections.sort((a, b) => (a.name || a.label || `Section ${a.id}`).localeCompare(b.name || b.label || `Section ${b.id}`));
        if (!searchValues.section) return sectionsList;
        return sectionsList.filter(section => {
            const sectionName = (section.name || section.label || `Section ${section.id}`).toLowerCase();
            return sectionName.includes(searchValues.section.toLowerCase());
        });
    };

    const getFilteredPlots = () => {
        const plotsList = plots.sort((a, b) => (a.name || `Plot ${a.id}`).localeCompare(b.name || `Plot ${b.id}`));
        if (!searchValues.plot) return plotsList;
        return plotsList.filter(plot => {
            const plotName = (plot.name || `Plot ${plot.id}`).toLowerCase();
            return plotName.includes(searchValues.plot.toLowerCase());
        });
    };

    const getFilteredCrops = () => {
        const cropsList = crops.sort((a, b) => {
            const aName = `${a.variety_name || 'Unknown'} - ${a.plant_type || 'Unknown'}`;
            const bName = `${b.variety_name || 'Unknown'} - ${b.plant_type || 'Unknown'}`;
            return aName.localeCompare(bName);
        });
        if (!searchValues.crop) return cropsList;
        return cropsList.filter(crop => {
            const cropName = `${crop.variety_name || 'Unknown'} - ${crop.plant_type || 'Unknown'}`.toLowerCase();
            return cropName.includes(searchValues.crop.toLowerCase());
        });
    };

    // Navigation handlers
    const handleFarmSelect = (id) => {
        handleMenuClose('farm');
        if (id === 'all') {
            setCurrentFarm(null);
            navigate(`/farm`);
        } else {
            const newFarm = farmsList.find(f => f.id === id && !f.deleted);
            if (newFarm) {
                setCurrentFarm(newFarm);
                navigate(`/farm/${newFarm.id}`, { 
                    state: { 
                        idToken,
                        zones: newFarm.sections || newFarm.zones || [],
                        farmLocations: newFarm.locations || [],
                        userData: farmData?.userData
                    } 
                });
            }
        }
    };
    const handleSectionSelect = (id) => {
        handleMenuClose('section');
        if (id === 'all') {
            navigate(location.pathname, {
                state: { ...location.state, section_id: undefined, plot_id: undefined, crop_id: undefined }
            });
        } else {
            navigate(location.pathname, {
                state: { ...location.state, section_id: id, plot_id: undefined, crop_id: undefined }
            });
        }
    };
    const handlePlotSelect = (id) => {
        handleMenuClose('plot');
        if (id === 'all') {
            navigate(location.pathname, {
                state: { ...location.state, plot_id: undefined, crop_id: undefined }
            });
        } else {
            navigate(location.pathname, {
                state: { ...location.state, plot_id: id, crop_id: undefined }
            });
        }
    };
    const handleCropSelect = (id) => {
        handleMenuClose('crop');
        if (id === 'all') {
            // Reset to default date range (Jan 1 of current year to today)
            const now = new Date();
            const jan1 = new Date(now.getFullYear(), 0, 1);
            setStartDate(jan1);
            setEndDate(now);
            
            navigate(location.pathname, {
                state: { ...location.state, crop_id: undefined }
            });
        } else {
            // Find the selected crop
            const selectedCrop = crops.find(c => c.id === id);
            if (selectedCrop) {
                const dateRange = getCropDateRange(selectedCrop);
                if (dateRange && setStartDate && setEndDate) {
                    // Set date range to crop's transplant date to last harvest date
                    setStartDate(dateRange.start);
                    setEndDate(dateRange.end);
                }
            }
            
            navigate(location.pathname, {
                state: { ...location.state, crop_id: id }
            });
        }
    };

    if (isMobile) {
        return (
            <Box sx={{ position: 'relative', width: '100%', minHeight: 36, mb: 1, px: 2, py: 0, bgcolor: 'transparent', borderRadius: 2 }}>
                {/* Top row: Logo, farm select, map icon */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mb: 1 }}>
                    {/* Logo on the left */}
                    <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                        <img src={Logo} alt="Logo" style={{ width: 28, height: 28, borderRadius: 16 }} />
                    </Box>
                    {/* Farm select */}
                    <StyledBreadcrumb
                        label={selectedFarm.name || selectedFarm.farm_name || 'All Farms'}
                        onDelete={e => handleMenuOpen('farm', e)}
                        deleteIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                        sx={{ flex: 1, mx: 1, minWidth: 0, justifyContent: 'center' }}
                    />
                    {/* Map icon button on the right */}
                    <IconButton onClick={() => navigate('/home')} sx={{ p: 1, ml: 1 }}>
                        <MapIcon sx={{ fontSize: 32, color: '#AAB4C8' }} />
                    </IconButton>
                </Box>
                {/* Second row: zone, plot, crop selectors (no separators) */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                    <StyledBreadcrumb
                        label={selectedSection.name || selectedSection.label || 'Section'}
                        onDelete={e => handleMenuOpen('section', e)}
                        deleteIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                        sx={{ minWidth: 0 }}
                    />
                    <StyledBreadcrumb
                        label={selectedPlot.name || 'Plot'}
                        onDelete={e => handleMenuOpen('plot', e)}
                        deleteIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                        sx={{ minWidth: 0 }}
                    />
                    {!hideCropSelector && (
                        <StyledBreadcrumb
                            label={
                                crop_id
                                    ? crops.find(c => c.id === crop_id)?.variety_name ||
                                    crops.find(c => c.id === crop_id)?.plant_type ||
                                    `Crop ${crop_id}`
                                    : 'Crop'
                            }
                            onDelete={e => handleMenuOpen('crop', e)}
                            deleteIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                            sx={{ minWidth: 0 }}
                        />
                    )}
                </Box>
                {/* Third row: date pickers on same line, centered */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    {/* Date pickers removed - moved to metrics cards container */}
                </Box>
                {/* MENUS MOVED BELOW */}
                <SearchableMenu
                    anchorEl={anchorEl.farm}
                    open={Boolean(anchorEl.farm)}
                    onClose={() => handleMenuClose('farm')}
                    searchValue={searchValues.farm}
                    onSearchChange={(value) => handleSearchChange('farm', value)}
                    searchPlaceholder="Search farms..."
                >
                    <MenuItem onClick={() => handleFarmSelect('all')}>All Farms</MenuItem>
                    {getFilteredFarms().map(farm => (
                        <MenuItem key={farm.id} onClick={() => handleFarmSelect(farm.id)}>
                            {farm.name || farm.farm_name || `Farm ${farm.id}`}
                        </MenuItem>
                    ))}
                </SearchableMenu>
                <SearchableMenu
                    anchorEl={anchorEl.section}
                    open={Boolean(anchorEl.section)}
                    onClose={() => handleMenuClose('section')}
                    searchValue={searchValues.section}
                    onSearchChange={(value) => handleSearchChange('section', value)}
                    searchPlaceholder="Search sections..."
                >
                    <MenuItem onClick={() => handleSectionSelect('all')}>All Sections</MenuItem>
                    {getFilteredSections().map(section => (
                        <MenuItem key={section.id} onClick={() => handleSectionSelect(section.id)}>
                            {section.name || section.label || `Section ${section.id}`}
                        </MenuItem>
                    ))}
                </SearchableMenu>
                <SearchableMenu
                    anchorEl={anchorEl.plot}
                    open={Boolean(anchorEl.plot)}
                    onClose={() => handleMenuClose('plot')}
                    searchValue={searchValues.plot}
                    onSearchChange={(value) => handleSearchChange('plot', value)}
                    searchPlaceholder="Search plots..."
                >
                    <MenuItem onClick={() => handlePlotSelect('all')}>All Plots</MenuItem>
                    {getFilteredPlots().map(plot => (
                        <MenuItem key={plot.id} onClick={() => handlePlotSelect(plot.id)}>
                            {plot.name || `Plot ${plot.id}`}
                        </MenuItem>
                    ))}
                </SearchableMenu>
                {!hideCropSelector && (
                    <SearchableMenu
                        anchorEl={anchorEl.crop}
                        open={Boolean(anchorEl.crop)}
                        onClose={() => handleMenuClose('crop')}
                        searchValue={searchValues.crop}
                        onSearchChange={(value) => handleSearchChange('crop', value)}
                        searchPlaceholder="Search crops..."
                    >
                        <MenuItem onClick={() => handleCropSelect('all')}>All Crops</MenuItem>
                        {getFilteredCrops().map(crop => {
                            const lastHarvestDate = getLastHarvestDate(crop);
                            const transplantDate = crop.transplant_date ? new Date(crop.transplant_date).toLocaleDateString() : 'N/A';
                            const harvestDate = lastHarvestDate ? lastHarvestDate.toLocaleDateString() : 'No harvests';
                            
                            return (
                                <MenuItem key={crop.id} onClick={() => handleCropSelect(crop.id)}>
                                    {`${crop.variety_name || 'Unknown'} - ${crop.plant_type || 'Unknown'} (${transplantDate} - ${harvestDate})`}
                                </MenuItem>
                            );
                        })}
                    </SearchableMenu>
                )}
            </Box>
        );
    }

    // Desktop layout (unchanged)
    return (
        <Box sx={{ position: 'relative', width: '100%', minHeight: 36, mb: 1, px: 4, py: 0.5, bgcolor: 'transparent', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 2, mt: -0.5 }}>
                <Breadcrumbs aria-label="breadcrumb" separator=">" sx={{ height: 32 }}>
                    <StyledBreadcrumb
                        label={selectedFarm.name || selectedFarm.farm_name || 'All Farms'}
                        onDelete={e => handleMenuOpen('farm', e)}
                        deleteIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                    />
                    <StyledBreadcrumb
                        label={selectedSection.name || selectedSection.label || 'All Sections'}
                        onDelete={e => handleMenuOpen('section', e)}
                        deleteIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                    />
                    <StyledBreadcrumb
                        label={selectedPlot.name || 'All Plots'}
                        onDelete={e => handleMenuOpen('plot', e)}
                        deleteIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                    />
                    {!hideCropSelector && (
                        <StyledBreadcrumb
                            label={
                                crop_id
                                    ? crops.find(c => c.id === crop_id)?.variety_name ||
                                    crops.find(c => c.id === crop_id)?.plant_type ||
                                    `Crop ${crop_id}`
                                    : 'All Crops'
                            }
                            onDelete={e => handleMenuOpen('crop', e)}
                            deleteIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
                        />
                    )}
                </Breadcrumbs>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Date pickers removed - moved to metrics cards container */}
                </Box>
                <MapBreadcrumb
                    icon={<MapIcon sx={{ fontSize: 18 }} />}
                    label="Map View"
                    clickable
                    onClick={() => navigate('/home')}
                />
            </Box>

            {/* MENUS MOVED BELOW */}
            <SearchableMenu
                anchorEl={anchorEl.farm}
                open={Boolean(anchorEl.farm)}
                onClose={() => handleMenuClose('farm')}
                searchValue={searchValues.farm}
                onSearchChange={(value) => handleSearchChange('farm', value)}
                searchPlaceholder="Search farms..."
            >
                <MenuItem onClick={() => handleFarmSelect('all')}>All Farms</MenuItem>
                {getFilteredFarms().map(farm => (
                    <MenuItem key={farm.id} onClick={() => handleFarmSelect(farm.id)}>
                        {farm.name || farm.farm_name || `Farm ${farm.id}`}
                    </MenuItem>
                ))}
            </SearchableMenu>

            <SearchableMenu
                anchorEl={anchorEl.section}
                open={Boolean(anchorEl.section)}
                onClose={() => handleMenuClose('section')}
                searchValue={searchValues.section}
                onSearchChange={(value) => handleSearchChange('section', value)}
                searchPlaceholder="Search sections..."
            >
                <MenuItem onClick={() => handleSectionSelect('all')}>All Sections</MenuItem>
                {getFilteredSections().map(section => (
                    <MenuItem key={section.id} onClick={() => handleSectionSelect(section.id)}>
                        {section.name || section.label || `Section ${section.id}`}
                    </MenuItem>
                ))}
            </SearchableMenu>

            <SearchableMenu
                anchorEl={anchorEl.plot}
                open={Boolean(anchorEl.plot)}
                onClose={() => handleMenuClose('plot')}
                searchValue={searchValues.plot}
                onSearchChange={(value) => handleSearchChange('plot', value)}
                searchPlaceholder="Search plots..."
            >
                <MenuItem onClick={() => handlePlotSelect('all')}>All Plots</MenuItem>
                {getFilteredPlots().map(plot => (
                    <MenuItem key={plot.id} onClick={() => handlePlotSelect(plot.id)}>
                        {plot.name || `Plot ${plot.id}`}
                    </MenuItem>
                ))}
            </SearchableMenu>

            {!hideCropSelector && (
                <SearchableMenu
                    anchorEl={anchorEl.crop}
                    open={Boolean(anchorEl.crop)}
                    onClose={() => handleMenuClose('crop')}
                    searchValue={searchValues.crop}
                    onSearchChange={(value) => handleSearchChange('crop', value)}
                    searchPlaceholder="Search crops..."
                >
                    <MenuItem onClick={() => handleCropSelect('all')}>All Crops</MenuItem>
                    {getFilteredCrops().map(crop => {
                        const lastHarvestDate = getLastHarvestDate(crop);
                        const transplantDate = crop.transplant_date ? new Date(crop.transplant_date).toLocaleDateString() : 'N/A';
                        const harvestDate = lastHarvestDate ? lastHarvestDate.toLocaleDateString() : 'No harvests';
                        
                        return (
                            <MenuItem key={crop.id} onClick={() => handleCropSelect(crop.id)}>
                                {`${crop.variety_name || 'Unknown'} - ${crop.plant_type || 'Unknown'} (${transplantDate} - ${harvestDate})`}
                            </MenuItem>
                        );
                    })}
                </SearchableMenu>
            )}
        </Box>
    );
};

export default FarmsNav;